
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from './Reveal';
import { ChevronLeft, ChevronRight, ShoppingBag, Truck, TrendingUp } from 'lucide-react';

export const TradeCaseStudies: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const cases = [
    {
      title: t('trade.case_studies.items.ecommerce.title'),
      icon: <ShoppingBag size={32} />,
      stat: t('trade.case_studies.items.ecommerce.stat'),
      statLabel: t('trade.case_studies.items.ecommerce.statLabel'),
      quote: t('trade.case_studies.items.ecommerce.quote'),
      author: t('trade.case_studies.items.ecommerce.author'),
      role: t('trade.case_studies.items.ecommerce.role')
    },
    {
      title: t('trade.case_studies.items.logistics.title'),
      icon: <Truck size={32} />,
      stat: t('trade.case_studies.items.logistics.stat'),
      statLabel: t('trade.case_studies.items.logistics.statLabel'),
      quote: t('trade.case_studies.items.logistics.quote'),
      author: t('trade.case_studies.items.logistics.author'),
      role: t('trade.case_studies.items.logistics.role')
    },
    {
      title: t('trade.case_studies.items.pos.title'),
      icon: <TrendingUp size={32} />,
      stat: t('trade.case_studies.items.pos.stat'),
      statLabel: t('trade.case_studies.items.pos.statLabel'),
      quote: t('trade.case_studies.items.pos.quote'),
      author: t('trade.case_studies.items.pos.author'),
      role: t('trade.case_studies.items.pos.role')
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % cases.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? cases.length - 1 : prev - 1));
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, []);

  const getSlidePositionClass = (index: number) => {
    if (index === currentIndex) return 'translate-x-0 opacity-100 z-20 scale-100';
    const prevIndex = (currentIndex - 1 + cases.length) % cases.length;
    if (index === prevIndex) return '-translate-x-full opacity-0 z-10 scale-95';
    const nextIndex = (currentIndex + 1) % cases.length;
    if (index === nextIndex) return 'translate-x-full opacity-0 z-10 scale-95';
    return 'translate-x-full opacity-0 z-0 hidden';
  };

  return (
    <section className="py-40 bg-rabbit-navy text-white border-y border-gray-800 relative overflow-hidden">
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-900/20 blur-[120px] pointer-events-none rounded-full"></div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">

        <Reveal>
          <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-800 pb-8">
            <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
              {t('trade.case_studies.title_line1')}<br />{t('trade.case_studies.title_line2')}
            </h2>
            <div className="flex gap-4 mt-8 md:mt-0">
              <button onClick={prevSlide} className="p-4 border border-gray-700 hover:bg-white hover:text-black hover:border-white transition-all duration-300 rounded-full group">
                <ChevronLeft size={24} className="text-gray-400 group-hover:text-black transition-colors" />
              </button>
              <button onClick={nextSlide} className="p-4 border border-gray-700 hover:bg-white hover:text-black hover:border-white transition-all duration-300 rounded-full group">
                <ChevronRight size={24} className="text-gray-400 group-hover:text-black transition-colors" />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="relative min-h-[600px] md:min-h-[450px]">
          {cases.map((item, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)] transform ${getSlidePositionClass(index)}`}
            >
              <div className="grid md:grid-cols-12 gap-12 items-center h-full">
                <div className="md:col-span-7 flex flex-col h-full justify-center">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="p-3 bg-white/10 rounded-lg text-emerald-400 border border-white/10">
                      {item.icon}
                    </div>
                    <span className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-200">{item.title}</span>
                  </div>
                  <blockquote className="text-2xl md:text-4xl font-light leading-[1.5] mb-12 text-white font-sans tracking-wide">
                    "{item.quote}"
                  </blockquote>
                  <div className="pl-4 border-l-2 border-emerald-500">
                    <div className="font-bold text-lg text-white">{item.author}</div>
                    <div className="text-gray-400 text-sm">{item.role}</div>
                  </div>
                </div>

                <div className="md:col-span-5 flex justify-start md:justify-end items-center">
                  <div className="bg-[#0F172A] p-12 w-full text-center border border-gray-800 relative overflow-hidden group hover:border-emerald-900 transition-colors">
                    <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
                    <div className="text-6xl md:text-7xl font-bold text-emerald-500 mb-4 tracking-tight group-hover:scale-110 transition-transform duration-500">{item.stat}</div>
                    <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">{item.statLabel}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-0 mt-12 md:mt-0 h-1 w-full bg-gray-800 max-w-xs">
          {cases.map((_, idx) => (
            <div key={idx} className="flex-1 h-full bg-gray-800 relative overflow-hidden border-r border-rabbit-navy last:border-0">
              <div
                className={`absolute inset-0 bg-emerald-500 transition-transform duration-[8000ms] linear ${idx === currentIndex ? 'translate-x-0' : '-translate-x-full'}`}
                style={{ transitionProperty: idx === currentIndex ? 'transform' : 'none' }}
              ></div>
              <div className={`absolute inset-0 bg-white/20 transition-opacity duration-300 ${idx === currentIndex ? 'opacity-100' : 'opacity-0'}`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
